import { Component, ElementRef, HostListener, Input, forwardRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * Self-rendered date picker (yyyy-MM-dd string value, drop-in for [(ngModel)]).
 * Exists specifically to avoid the browser/OS native <input type="date"> calendar popup,
 * which has been reported as slow to open and intermittently rendering solid black —
 * a known Chromium/Windows native-control compositing issue outside app CSS control.
 */
@Component({
  selector: 'app-date-input',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './date-input.component.html',
  styleUrl: './date-input.component.css',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateInputComponent),
      multi: true
    }
  ]
})
export class DateInputComponent implements ControlValueAccessor {
  @Input() placeholder = 'Select date';

  isOpen = false;
  value: string = '';
  viewYear = new Date().getFullYear();
  viewMonth = new Date().getMonth();
  disabled = false;

  private onChange: (value: string) => void = () => { };
  private onTouched: () => void = () => { };

  readonly weekdayLabels = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

  constructor(private elementRef: ElementRef<HTMLElement>) { }

  writeValue(value: string): void {
    this.value = value || '';
    const parsed = this.parseValue(this.value);
    if (parsed) {
      this.viewYear = parsed.getFullYear();
      this.viewMonth = parsed.getMonth();
    }
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  get displayLabel(): string {
    const parsed = this.parseValue(this.value);
    if (!parsed) {
      return this.placeholder;
    }
    return parsed.toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
  }

  get viewMonthLabel(): string {
    return new Date(this.viewYear, this.viewMonth, 1).toLocaleDateString('en-IN', { year: 'numeric', month: 'long' });
  }

  get calendarCells(): { date: number; iso: string; isCurrentMonth: boolean; isSelected: boolean; isToday: boolean }[] {
    const firstOfMonth = new Date(this.viewYear, this.viewMonth, 1);
    const startOffset = firstOfMonth.getDay();
    const gridStart = new Date(this.viewYear, this.viewMonth, 1 - startOffset);
    const todayIso = this.formatIso(new Date());

    const cells = [];
    for (let i = 0; i < 42; i++) {
      const cellDate = new Date(gridStart.getFullYear(), gridStart.getMonth(), gridStart.getDate() + i);
      const iso = this.formatIso(cellDate);
      cells.push({
        date: cellDate.getDate(),
        iso,
        isCurrentMonth: cellDate.getMonth() === this.viewMonth,
        isSelected: iso === this.value,
        isToday: iso === todayIso
      });
    }
    return cells;
  }

  toggleOpen(): void {
    if (this.disabled) {
      return;
    }
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      const parsed = this.parseValue(this.value) || new Date();
      this.viewYear = parsed.getFullYear();
      this.viewMonth = parsed.getMonth();
    } else {
      this.onTouched();
    }
  }

  selectDay(iso: string): void {
    this.value = iso;
    this.onChange(iso);
    this.isOpen = false;
    this.onTouched();
  }

  previousMonth(): void {
    const d = new Date(this.viewYear, this.viewMonth - 1, 1);
    this.viewYear = d.getFullYear();
    this.viewMonth = d.getMonth();
  }

  nextMonth(): void {
    const d = new Date(this.viewYear, this.viewMonth + 1, 1);
    this.viewYear = d.getFullYear();
    this.viewMonth = d.getMonth();
  }

  clear(event: Event): void {
    event.stopPropagation();
    this.value = '';
    this.onChange('');
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.isOpen && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.isOpen = false;
      this.onTouched();
    }
  }

  private parseValue(value: string): Date | null {
    if (!value) {
      return null;
    }
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }
    return new Date(year, month - 1, day);
  }

  private formatIso(date: Date): string {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }
}
